import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "@/components/Logo";
import { LuxeLink } from "@/components/LuxeLink";

const LINKS = [
  { to: "/collection", label: "Collection" },
  { to: "/selection", label: "The Standard" },
  { to: "/private-collection", label: "How It Works" },
  { to: "/apply", label: "Apply" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-700 ${
        scrolled ? "bg-navy-900/90 backdrop-blur-md border-b border-ivory/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-20 md:h-24">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center">
          <Logo variant="wordmark" className="h-8 md:h-9" />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[11px] tracking-[0.22em] uppercase transition-colors duration-500 ${
                  isActive ? "text-gold" : "text-ivory/70 hover:text-ivory"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <LuxeLink to="/invitation" variant="outline" data-testid="nav-invitation-cta">
            I Have an Invitation
          </LuxeLink>
        </div>

        <button
          className="lg:hidden text-ivory p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4 }}
            className="lg:hidden bg-navy-900/97 backdrop-blur-lg border-t border-ivory/10 px-6 py-8"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-6">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="text-ivory/80 text-sm tracking-[0.2em] uppercase"
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </Link>
              ))}
              <LuxeLink to="/invitation" variant="solid" className="mt-2 w-full">
                I Have an Invitation
              </LuxeLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
